import { useMemo } from 'react';

import { EditRolesContainer } from './EditRoles.container';

import { FormItem, Spacer } from '@aircall/tractor';
import {
  FormItem as FormItemV2,
  ThemeProvider as ThemeProviderV2,
  Select as SelectV2,
} from '@aircall/tractor-v2';
import { GridItem, GridLayout } from '@components/GridLayout/GridLayout';
import { SelectOption } from '@components/OldSelect';
import { PaperForm } from '@components/PaperForm';
import { TextField } from '@components/TextField/TextField';
import { LANGUAGES_ISO_CODES } from '@constants/languages.constants';
import { pipeValidators, validateNumberOnly, validateRequired } from '@dashboard/library';
import { UserFullFragment } from '@generated/UserFullFragment';
import { useUpdateUserInformation } from '@hooks/useUpdateUserInformation';
import { UserProfileTabProps } from '@pages/user-profile/UserProfile.decl';
import { TFunction } from 'i18next';
import { Field } from 'react-final-form';
import { useTranslation } from 'react-i18next';

interface ProfileSettingsSectionProps extends UserProfileTabProps {
  isCurrentUser: boolean;
}

export function getLanguageSelectOptions(t: TFunction) {
  return LANGUAGES_ISO_CODES.reduce<SelectOption[]>(
    (acc, value) => [...acc, { label: t(`app.languages.${value}`), value }],
    []
  );
}

export function ProfileSettingsSection({ user, isCurrentUser }: ProfileSettingsSectionProps) {
  const { t } = useTranslation();
  const languageSelectOptions = useMemo(() => getLanguageSelectOptions(t), [t]);
  const { updateUser } = useUpdateUserInformation(user, isCurrentUser);

  return (
    <Spacer fluid direction='vertical' space='m' data-test='profile-settings-section'>
      <PaperForm<UserFullFragment>
        shouldBlockNavigation
        title={t('user_profile.general.profile_settings.title')}
        formProps={{
          onSubmit: updateUser,
          initialValues: user,
        }}
      >
        {() => (
          <GridLayout gridColumnGap={{ _: 'm', md: 'l' }} gridRowGap='m'>
            <GridItem xs={12}>
              <FormItem label={t('app.currentRoles.label')}>
                <EditRolesContainer isCurrentUser={isCurrentUser} user={user} />
              </FormItem>
            </GridItem>

            <GridItem xs={6}>
              <ThemeProviderV2>
                <FormItemV2 label={t('app.languages.label')}>
                  <Field name='language'>
                    {({ input: { onChange, value } }) => (
                      <SelectV2
                        w='100%'
                        // Placeholder will always be english
                        placeholder={t(`app.languages.en-US`)}
                        selectedKeys={[value]}
                        onSelectionChange={([selectedKey]) => onChange(selectedKey)}
                        options={languageSelectOptions}
                        data-test='user-profile-select-language'
                      />
                    )}
                  </Field>
                </FormItemV2>
              </ThemeProviderV2>
            </GridItem>

            <GridItem xs={6}>
              <TextField
                name='extension'
                label={t('app.extension')}
                validate={pipeValidators(validateRequired, validateNumberOnly)}
                maxLength={3}
                data-test='user-profile-extension'
                size='regular'
              />
            </GridItem>
          </GridLayout>
        )}
      </PaperForm>
    </Spacer>
  );
}
